/* eslint-disable no-param-reassign */
import {Chip} from '@dropbox/dig-components/chip';
import {Menu} from '@dropbox/dig-components/menu';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  CalendarLine,
  CheckmarkLine,
  DeleteLine,
  FlagLine,
  MetricsLine,
  PersonLine,
  PersonMultipleLine,
  WorkstreamsLine,
} from '@dropbox/dig-icons/assets';
import {
  $createTableCellNode,
  $createTableRowNode,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
  TableNode,
  TableRowNode,
} from '@lexical/table';
import {
  EditordocService,
  EditorDocTemplateType,
  EditorWorkstreamBasic,
  EditorWorkstreamType,
  EmployeeLdap,
  WorkstreamBasic,
  WorkstreamBasicWithOutcomes,
  WorkstreamMetric,
  WorkstreamPrivacy,
  WorkstreamStatus,
} from 'client';
import {useCollabContext, usePlanningId} from 'components/DSYS/CollabProvider';
import {sourceTextKeyFor} from 'components/DSYS/editor/dynamicNode/overlayHelpers';
import {WorkstreamNodeType} from 'components/DSYS/editor/utils';
import {parseAndExtractTextFromLexicalJson} from 'components/DSYS/editor/utils/lexical';
import {t} from 'i18next';
import {
  $getNodeByKey,
  $getRoot,
  $isParagraphNode,
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import {useIsTemplateMode} from 'plugins/enhancedHeadings';
import {type ReactNode, useEffect, useState} from 'react';
import {getService} from 'utilities';
import {
  useEditorWorkstreams,
  useIsPlanningDocPublished,
  usePlanning,
  useWorkstreamsUpdate,
} from 'views/planning/hooks';
import {getMaxNumOfEditorDocs} from 'views/planning/utils';
import {queryClient} from 'views/QueryClientWrapper';
import {useWorkstream} from 'views/workstreams/hooks';
import {
  DatePickerEditor,
  EmployeeSelect,
  MultiEmployeeSelect,
} from 'views/workstreams/TODODSYSStuff';
import {
  getPrimaryMetricForWorkstreamType,
  getSoonestMetricDate,
  getSoonestMetricTarget,
  getWorkstreamDri,
  getWorkstreamExecSponsor,
  getWorkstreamWorkingGroup,
  isPrimaryMetric,
  toShortDate,
} from 'views/workstreams/utils';

import styles from './TypeaheadNode.module.css';
import {
  AddWorkstreamComponent,
  ContributingWorkstreamEditComponent,
  WorkstreamEditComponentWrapper,
  WorkstreamMetricEditComponent,
  WorkstreamTypeaheadSelectionWrapper,
} from './WorkstreamEditComponent';

type TypeaheadNodeType =
  | 'owner'
  | 'employee'
  | 'date'
  | 'workstream'
  | 'team_members'
  | WorkstreamNodeType;
type WorkstreamType = 'workstream' | 'switchboard' | 'refresh';
type TypeaheadNodeValue =
  | EmployeeLdap
  | EmployeeLdap[] // for 'team_members' type
  | string
  | string[] // for repeatable ordered-list types
  | WorkstreamBasic
  | WorkstreamBasic[] // for 'workstream' type
  | WorkstreamType
  | undefined;

// ---------- Node ----------
type SerializedTypeaheadNode = Spread<
  {
    type: 'typeahead';
    version: 1;
    nodeType: TypeaheadNodeType;
    value: TypeaheadNodeValue;
    contentEditorState?: string;
  },
  SerializedLexicalNode
>;

const getWorkstreamIdFromNodeValue = (value: TypeaheadNodeValue): number | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const workstreamId =
    'workstream_id' in value && typeof value.workstream_id === 'number'
      ? value.workstream_id
      : 'id' in value && typeof value.id === 'number'
        ? value.id
        : undefined;

  return workstreamId;
};

const getPlainTextObjectiveExport = (value: TypeaheadNodeValue): string => {
  const description = (value as {description?: string | null})?.description;
  return description ? parseAndExtractTextFromLexicalJson(description) : '';
};

const isMetricCompletedForRedesignExport = (metric: WorkstreamMetric) => {
  const status = (metric.status ?? '').trim().toLowerCase();
  return (
    metric.complete === true || status === 'completed' || status === 'done' || status === 'missed'
  );
};

const appendRedesignOutcomeExportHtml = (
  el: HTMLElement,
  metrics: WorkstreamMetric[],
  isLongtermCell: boolean
) => {
  const scoped = metrics.filter((m) => m.is_longterm === isLongtermCell && m.type !== 'metric');
  if (scoped.length === 0) return;

  const active = scoped.filter((m) => !isMetricCompletedForRedesignExport(m));
  const completed = scoped.filter((m) => isMetricCompletedForRedesignExport(m));

  const formatMetricHtml = (metric: WorkstreamMetric) => {
    const titlePlain = parseAndExtractTextFromLexicalJson(metric.title) || metric.title;
    let line = titlePlain;
    if (metric.owner) {
      line += `<br>• DRI: ${metric.owner.name}`;
    }
    const metricDate = getSoonestMetricDate(metric);
    if (metricDate) {
      line += `<br>• ETA: ${toShortDate(new Date(metricDate))}`;
    }
    if (metric.current != null || getSoonestMetricTarget(metric) != null) {
      line += `<br>• Metric: ${metric.current ?? 0} / ${getSoonestMetricTarget(metric) ?? 0}`;
    }
    return line + '<br>';
  };

  const sectionTitle = isLongtermCell ? t('definition_of_done') : t('outcomes');
  let wroteContent = false;
  if (active.length > 0) {
    el.innerHTML += `${wroteContent ? '<br>' : ''}<b>${sectionTitle}</b><br>`;
    el.innerHTML += active.map(formatMetricHtml).join('<br>');
    wroteContent = true;
  }
  if (completed.length > 0) {
    el.innerHTML += `${wroteContent ? '<br>' : ''}<b>${t('closed')}</b><br>`;
    el.innerHTML += completed.map(formatMetricHtml).join('<br>');
  }
};

const appendRedesignMetricsExportHtml = (el: HTMLElement, metrics: WorkstreamMetric[]) => {
  const scoped = metrics.filter((m) => m.type === 'metric');
  if (scoped.length === 0) return;

  el.innerHTML += `<b>${t('metrics')}</b><br>`;
  for (const metric of scoped) {
    const titlePlain = parseAndExtractTextFromLexicalJson(metric.title) || metric.title;
    let line = titlePlain;
    if (metric.owner) {
      line += `<br>• DRI: ${metric.owner.name}`;
    }
    const metricDate = getSoonestMetricDate(metric);
    if (metricDate) {
      line += `<br>• ETA: ${toShortDate(new Date(metricDate))}`;
    }
    if (metric.current != null || getSoonestMetricTarget(metric) != null) {
      line += `<br>• ${t('current')}: ${metric.current ?? ''} / ${t('target')}: ${getSoonestMetricTarget(metric) ?? ''}`;
    }
    el.innerHTML += `${line}<br><br>`;
  }
};

const appendMetricSummaryExportHtml = (el: HTMLElement, metric: WorkstreamMetric) => {
  const titlePlain = parseAndExtractTextFromLexicalJson(metric.title) || metric.title;
  let line = titlePlain;
  if (metric.owner) {
    line += `<br>• DRI: ${metric.owner.name}`;
  }
  const metricDate = getSoonestMetricDate(metric);
  if (metricDate) {
    line += `<br>• ETA: ${toShortDate(new Date(metricDate))}`;
  }
  if (metric.current != null || getSoonestMetricTarget(metric) != null) {
    line += `<br>• ${t('current')}: ${metric.current ?? ''} / ${t('target')}: ${getSoonestMetricTarget(metric) ?? ''}`;
  }

  el.innerHTML += `${line}<br>`;
};

const appendOutcomeSummaryExportHtml = (el: HTMLElement, outcome: WorkstreamMetric) => {
  const titlePlain = parseAndExtractTextFromLexicalJson(outcome.title) || outcome.title;
  let line = titlePlain;
  if (outcome.owner) {
    line += `<br>• DRI: ${outcome.owner.name}`;
  }
  if (outcome.complete_date) {
    line += `<br>• ETA: ${toShortDate(new Date(outcome.complete_date))}`;
  }

  el.innerHTML += `${line}<br>`;
};

function convertTypeaheadElement(domNode: HTMLElement): DOMConversionOutput | null {
  const nodeType = domNode.getAttribute('data-node-type');
  if (nodeType) {
    const node = $createTypeaheadNode(nodeType as TypeaheadNodeType);
    return {node};
  }
  return null;
}

export class TypeaheadNode extends DecoratorNode<ReactNode> {
  __value: TypeaheadNodeValue;
  __nodeType: TypeaheadNodeType;
  __contentEditorState?: string;

  static getType() {
    return 'typeahead';
  }

  static clone(node: TypeaheadNode) {
    const cloned = new TypeaheadNode(node.__nodeType, node.__value, node.__key);
    cloned.__contentEditorState = node.__contentEditorState;
    return cloned;
  }

  constructor(nodeType: TypeaheadNodeType, value?: TypeaheadNodeValue, key?: NodeKey) {
    super(key);
    this.__nodeType = nodeType;
    this.__value = value;
  }

  static importJSON(json: SerializedTypeaheadNode) {
    const node = new TypeaheadNode(json.nodeType, json.value);
    node.__contentEditorState = json.contentEditorState;
    return node;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (
          domNode.getAttribute('data-lexical-type') === 'typeahead' &&
          domNode.hasAttribute('data-node-type')
        ) {
          return {
            conversion: convertTypeaheadElement,
            priority: 1,
          };
        }
        return null;
      },
    };
  }

  isInline(): boolean {
    return true;
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return false;
  }

  getContentEditorState(): string {
    return this.__contentEditorState ?? '';
  }

  getTextContent(): string {
    if (this.__nodeType === 'workstream') {
      // Include narrative worsktream names in word count by returning text content
      let textContent = '';
      if (Array.isArray(this.__value)) {
        (this.__value as WorkstreamBasic[]).forEach((workstream) => {
          textContent += `${workstream.name} `;
        });
      }
      return textContent.trim();
    }
    return '';
  }

  setNodeType(nodeType: TypeaheadNodeType) {
    const self = this.getWritable();
    self.__nodeType = nodeType;
  }

  setContentEditorState(contentEditorState: string) {
    const self = this.getWritable();
    self.__contentEditorState = contentEditorState;
  }

  exportJSON(): SerializedTypeaheadNode {
    return {
      type: 'typeahead',
      version: 1,
      nodeType: this.__nodeType,
      value: this.__value,
      contentEditorState: this.__contentEditorState,
    };
  }

  createDOM(): HTMLElement {
    // DecoratorNode returns a container; React renders inside.
    return document.createElement('span');
  }

  updateDOM(): boolean {
    return false;
  }

  exportDOM(): DOMExportOutput {
    const el = document.createElement('span');

    if (this.__nodeType === 'employee' || this.__nodeType === 'owner') {
      el.textContent = (this.__value as EmployeeLdap)?.name ?? '';
    } else if (this.__nodeType === 'date') {
      el.textContent = (this.__value as string) ?? '';
    } else if (this.__nodeType === 'workstream-link' || this.__nodeType === 'switchboard-link') {
      el.textContent = (this.__value as string) ?? '';
    } else if (
      this.__nodeType === 'workstream' ||
      this.__nodeType === 'contributing-workstream-name'
    ) {
      // Display workstreams as formatted text with line breaks
      const workstreams = Array.isArray(this.__value) ? this.__value : [this.__value];
      const validWorkstreams = workstreams.filter(Boolean) as WorkstreamBasic[];

      if (validWorkstreams.length > 0) {
        const groups: string[] = [];
        validWorkstreams.forEach((workstream) => {
          const dri = getWorkstreamDri(workstream);
          if (dri) {
            groups.push(`${workstream.name}<br>• DRI: ${dri.name}`);
          } else {
            groups.push(workstream.name);
          }
        });
        // Join groups with double <br> for spacing between workstreams
        el.innerHTML = groups.join('<br><br>');
      }
    } else if (this.__nodeType === 'workstream-name' || this.__nodeType === 'switchboard-name') {
      el.textContent = (this.__value as WorkstreamBasic)?.name ?? '';
    } else if (
      this.__nodeType === 'workstream-objective-editable' ||
      this.__nodeType === 'switchboard-objective-editable'
    ) {
      el.textContent = getPlainTextObjectiveExport(this.__value);
    } else if (
      this.__nodeType === 'workstream-outcomes' ||
      this.__nodeType === 'workstream-definition-of-done'
    ) {
      const metrics = (this.__value as WorkstreamBasicWithOutcomes)?.metrics;
      if (metrics && metrics.length > 0) {
        const isLongtermCell = this.__nodeType === 'workstream-definition-of-done';
        appendRedesignOutcomeExportHtml(el, metrics, isLongtermCell);
      }
    } else if (this.__nodeType === 'workstream-metrics') {
      const metrics = (this.__value as WorkstreamBasicWithOutcomes)?.metrics;
      if (metrics && metrics.length > 0) {
        appendRedesignMetricsExportHtml(el, metrics);
      }
    } else if (
      this.__nodeType === 'workstream-primary-lead-metric' ||
      this.__nodeType === 'workstream-primary-lag-metric'
    ) {
      const workstream = this.__value as WorkstreamBasicWithOutcomes;
      const workstreamId = getWorkstreamIdFromNodeValue(workstream);
      const metricType =
        this.__nodeType === 'workstream-primary-lead-metric' ? 'leading' : 'lagging';
      if (workstreamId && workstream?.metrics?.length) {
        const metric = getPrimaryMetricForWorkstreamType(
          workstream.metrics,
          workstreamId,
          metricType
        );
        if (metric) {
          appendMetricSummaryExportHtml(el, metric);
        } else {
          el.textContent = '-';
        }
      } else {
        el.textContent = '-';
      }
    } else if (this.__nodeType === 'workstream-starred-milestones') {
      const workstream = this.__value as WorkstreamBasicWithOutcomes;
      const workstreamId = getWorkstreamIdFromNodeValue(workstream);
      const starredMilestones =
        workstreamId && workstream?.metrics?.length
          ? workstream.metrics
              .filter(
                (metric) => metric.type === 'milestone' && isPrimaryMetric(metric, workstreamId)
              )
              .sort((a, b) => {
                const aTime = a.complete_date ? new Date(a.complete_date).getTime() : Infinity;
                const bTime = b.complete_date ? new Date(b.complete_date).getTime() : Infinity;
                if (aTime !== bTime) {
                  return aTime - bTime;
                }
                return a.id - b.id;
              })
          : [];

      if (starredMilestones.length > 0) {
        starredMilestones.forEach((milestone, index) => {
          if (index > 0) {
            el.innerHTML += '<br>';
          }
          appendOutcomeSummaryExportHtml(el, milestone);
        });
      } else {
        el.textContent = '-';
      }
    } else if (this.__nodeType === 'workstream-dri' || this.__nodeType === 'switchboard-dri') {
      el.textContent = getWorkstreamDri(this.__value as WorkstreamBasic)?.name ?? '';
    } else if (
      this.__nodeType === 'workstream-exec-sponsor' ||
      this.__nodeType === 'switchboard-exec-sponsor'
    ) {
      el.textContent = getWorkstreamExecSponsor(this.__value as WorkstreamBasic)?.name ?? '';
    } else if (this.__nodeType === 'switchboard-working-group') {
      el.textContent = getWorkstreamWorkingGroup(this.__value as WorkstreamBasic)
        .map(({name}) => name)
        .join(', ');
    } else if (this.__nodeType === 'team_members') {
      const members = (this.__value as EmployeeLdap[]) ?? [];
      if (members.length > 0) {
        el.textContent = members.map((m) => m.name).join(', ');
      }
    } else if (
      this.__nodeType === 'workstream-subworkstreams' ||
      this.__nodeType === 'switchboard-subworkstreams' ||
      this.__nodeType === 'contributing-workstream-subworkstreams'
    ) {
      // Subworkstreams are stored in dependencies
      const workstream = this.__value as any;
      const dependencies = workstream?.dependencies;
      if (dependencies && dependencies.length > 0) {
        const groups: string[] = [];
        dependencies.forEach((dependency: any) => {
          const name = dependency.dependent?.name ?? dependency.name ?? '';
          const dri =
            this.__nodeType === 'contributing-workstream-subworkstreams'
              ? getWorkstreamDri(dependency)
              : getWorkstreamDri(dependency.dependent);
          if (dri) {
            groups.push(`${name}<br>• DRI: ${dri.name}`);
          } else {
            groups.push(name);
          }
        });
        // Join groups with double <br> for spacing between workstreams
        el.innerHTML = groups.join('<br><br>');
      }
    } else if (this.__nodeType === 'workstream-action') {
      // Action type
      const workstream = this.__value as any;
      el.textContent = workstream?.action_type ?? '';
    } else {
      el.textContent = '';
    }

    el.setAttribute('data-lexical-type', 'typeahead');
    el.setAttribute('data-node-type', this.__nodeType);
    return {element: el};
  }

  setValue(value: TypeaheadNodeValue) {
    const self = this.getWritable();
    self.__value = value;
  }

  decorate(editor: LexicalEditor): ReactNode {
    const typeIndex = getTypeheadNodeTypeIndex(editor, this.getKey(), this.__nodeType);
    return (
      <TypeaheadComponent
        nodeKey={this.getKey()}
        editor={editor}
        nodeType={this.__nodeType}
        initialValue={this.__value}
        initialContentEditorState={this.__contentEditorState}
        typeIndex={typeIndex}
      />
    );
  }
}

export function $createTypeaheadNode(nodeType: TypeaheadNodeType, value?: TypeaheadNodeValue) {
  return new TypeaheadNode(nodeType, value);
}

export function $isTypeaheadNode(node: LexicalNode | null | undefined): node is TypeaheadNode {
  return node instanceof TypeaheadNode;
}

export function getTypeheadNodeTypeIndex(
  editor: LexicalEditor,
  selfKey: NodeKey,
  selfType: TypeaheadNode['__nodeType']
): number {
  return editor.getEditorState().read(() => {
    const root = $getRoot();

    let idx = 0;
    let mine = -1;

    const stack: LexicalNode[] = [root];
    while (stack.length) {
      const node = stack.pop()!;

      if ($isTypeaheadNode(node) && (node as TypeaheadNode).__nodeType === selfType) {
        if (node.getKey() === selfKey) {
          mine = idx;
          break;
        }
        idx++;
      }

      const getChildren = (node as any).getChildren?.bind(node);
      if (getChildren) {
        const children: LexicalNode[] = getChildren();
        for (let i = children.length - 1; i >= 0; i--) stack.push(children[i]);
      }
    }

    return mine;
  });
}

function getWorkstreamTable(editor: LexicalEditor, nodeType: TypeaheadNodeType): TableNode | null {
  let table: TableNode | null = null;

  editor.getEditorState().read(() => {
    const root = $getRoot();
    const stack: LexicalNode[] = [root];

    while (stack.length) {
      const n = stack.pop()!;

      if ($isTypeaheadNode(n) && (n as TypeaheadNode).__nodeType === nodeType) {
        let curr: LexicalNode | null = n;
        while (curr && !$isTableNode(curr)) {
          curr = curr.getParent();
        }
        if (curr && $isTableNode(curr)) {
          table = curr;
        }
        break;
      }

      const getChildren = (n as any).getChildren?.bind(n);
      if (getChildren) {
        const children = getChildren() as LexicalNode[];
        for (let i = children.length - 1; i >= 0; i--) {
          stack.push(children[i]);
        }
      }
    }
  });

  return table;
}

export function getTypeaheadNodeTableContext(
  editor: LexicalEditor,
  nodeKey: NodeKey
): {
  bodyRowCount: number;
  rowIndex: number | null;
  workstreamNameNodeCount: number;
} {
  let context = {
    bodyRowCount: 0,
    rowIndex: null as number | null,
    workstreamNameNodeCount: 0,
  };

  editor.getEditorState().read(() => {
    const node = $getNodeByKey(nodeKey);
    if (!node || !$isTypeaheadNode(node)) return;

    let row: LexicalNode | null = node;
    while (row && !$isTableRowNode(row)) {
      row = row.getParent();
    }

    let table: LexicalNode | null = row;
    while (table && !$isTableNode(table)) {
      table = table.getParent();
    }

    if (!row || !table || !$isTableRowNode(row) || !$isTableNode(table)) return;

    const rows = table.getChildren().filter($isTableRowNode);
    const bodyRows = rows.slice(1);

    let workstreamNameNodeCount = 0;
    const stack: LexicalNode[] = [table];
    while (stack.length) {
      const current = stack.pop()!;
      if ($isTypeaheadNode(current) && current.__nodeType === 'workstream-name') {
        workstreamNameNodeCount += 1;
      }

      const getChildren = (current as any).getChildren?.bind(current);
      if (getChildren) {
        const children = getChildren() as LexicalNode[];
        for (let i = children.length - 1; i >= 0; i--) {
          stack.push(children[i]);
        }
      }
    }

    context = {
      bodyRowCount: bodyRows.length,
      rowIndex: bodyRows.findIndex((bodyRow) => bodyRow.getKey() === row.getKey()),
      workstreamNameNodeCount,
    };
  });

  return context;
}

export function syncWorkstreamTableRows(
  editor: LexicalEditor,
  planningId: string | undefined,
  desiredRows: number,
  nodeType: TypeaheadNodeType
) {
  editor.update(() => {
    if (!planningId) return;

    const table = getWorkstreamTable(editor, nodeType);
    if (!table || !$isTableNode(table)) return;

    const currentRowsCount = table.getChildren().length - 1; // Exclude header row
    if (currentRowsCount === desiredRows) return;

    if (desiredRows < currentRowsCount) {
      for (let i = currentRowsCount; i > desiredRows; i--) {
        table.getLastChild()?.remove();
      }
      return;
    }

    const templateRow = table.getLastChild() as TableRowNode | null;
    const colCount = templateRow?.getChildren().length ?? 0;
    if (!templateRow || colCount === 0) return;

    const newRow = $createTableRowNode();
    for (let c = 0; c < colCount; c++) {
      const newCell = $createTableCellNode(TableCellHeaderStates.NO_STATUS);
      const prevCell = templateRow.getChildAtIndex(c);
      if (prevCell && $isTableCellNode(prevCell)) {
        const cellNode = prevCell.getFirstChild();
        if (cellNode) {
          const prevTypeahead =
            cellNode && $isTypeaheadNode(cellNode)
              ? cellNode
              : cellNode && $isParagraphNode(cellNode)
                ? cellNode.getFirstChild()
                : undefined;
          if (prevTypeahead && $isTypeaheadNode(prevTypeahead)) {
            newCell.append($createTypeaheadNode(prevTypeahead.__nodeType, undefined));
          }
        }
      }
      newRow.append(newCell);
    }

    for (let i = table.getChildren().length - 1; i < desiredRows; i++) {
      table.append(newRow);
    }
  });
}

export function hideDeletedAndPrivateWorkstreamsInWorkstreamTable(
  editor: LexicalEditor,
  workstreamsInfo: EditorWorkstreamBasic[],
  nodeType: TypeaheadNodeType
) {
  editor.update(() => {
    const table = getWorkstreamTable(editor, nodeType);
    if (!table || !$isTableNode(table)) return;

    const rows = table.getChildren();
    for (let i = 0; i < workstreamsInfo.length; i++) {
      const row = rows[i + 1]; // Skip header row
      const workstreamInfo = workstreamsInfo[i];
      if (!row || !workstreamInfo) continue;
      if ($isTableRowNode(row)) {
        const isDeleted =
          workstreamInfo.archived && workstreamInfo.status !== WorkstreamStatus.DRAFT;
        const isPrivate = workstreamInfo.privacy == WorkstreamPrivacy.PRIVATE;
        const dom = editor.getElementByKey(row.getKey());
        if (dom) {
          dom.style.display = isDeleted || isPrivate ? 'none' : '';
        }
      }
    }
  });
}

// ---------- Rendered React component for the node ----------
const TypeaheadComponent = ({
  nodeKey,
  editor,
  nodeType,
  initialValue,
  initialContentEditorState,
  typeIndex,
}: {
  nodeKey: NodeKey;
  editor: LexicalEditor;
  nodeType: TypeaheadNodeType;
  initialValue: TypeaheadNodeValue;
  initialContentEditorState?: string;
  typeIndex: number;
}) => {
  const isTemplateMode = useIsTemplateMode();
  const {collabOwner, planningId} = useCollabContext();
  const {planning} = usePlanning(planningId ?? '');
  const isSwitchboardDoc =
    planning?.template_type === EditorDocTemplateType.SWITCHBOARD ||
    planning?.template_type === EditorDocTemplateType.REFRESH;
  const [value, setValue] = useState<TypeaheadNodeValue>(initialValue);

  useEffect(() => {
    if (collabOwner && nodeType === 'owner') {
      setValue(collabOwner);
    }
  }, [collabOwner, nodeType, setValue]);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const commitValue = (next: TypeaheadNodeValue) => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (node instanceof TypeaheadNode) node.setValue(next);
    });
  };

  const commitNodeType = (nextNodeType: TypeaheadNodeType | undefined) => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (node instanceof TypeaheadNode) {
        if (!nextNodeType) {
          node.remove();
        } else {
          node.setNodeType(nextNodeType);
        }
      }
    });
  };

  const handleEmployeeSelection = (selection?: EmployeeLdap) => {
    if (nodeType === 'owner' && !selection) {
      return;
    }

    setValue(selection);
    commitValue(selection);
    if (planningId && nodeType === 'owner') {
      getService(EditordocService).updateApiV1EditordocEncryptedIdPut(planningId, {
        owner_id: selection?.user_id,
      });
    }
  };

  const handleDateSelection = (date: string | null) => {
    setValue(date ?? undefined);
    commitValue(date ?? undefined);
  };

  const {mutateAsync: updateWorkstreams} = useWorkstreamsUpdate(planningId ?? '');
  const handleWorkstreamSelection = async (
    selection: WorkstreamBasic | undefined,
    workstreamType: EditorWorkstreamType = EditorWorkstreamType.NARRATIVE
  ) => {
    if (planningId && selection?.id) {
      await updateWorkstreams({workstreams: [selection.id], workstreamType})
        .then(() => {
          queryClient.invalidateQueries({queryKey: ['planning', 'workstreams', planningId]});
          setValue(selection);
          commitValue(selection);
        })
        .catch(() => {
          setValue(undefined);
          commitValue(undefined);
        });
    }
  };

  const handleWorkstreamRemove = async (
    workstreamId: number,
    workstreamType: EditorWorkstreamType
  ) => {
    if (planningId) {
      await updateWorkstreams({workstreams: [workstreamId], action: 'remove', workstreamType}).then(
        () => {
          setValue(undefined);
          commitValue(undefined);
        }
      );
    }
  };

  const handleContentEditorStateChange = (
    workstreamId: number,
    contentEditorState: string | undefined,
    sourceText?: string
  ) => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (node instanceof TypeaheadNode) {
        const oldContentEditorState = node.getContentEditorState();
        const oldContentEditorStateObj = JSON.parse(oldContentEditorState || '{}') as Record<
          string,
          string | undefined
        >;
        const mapKey = String(workstreamId);
        const newContentEditorStateObj: Record<string, string | undefined> = {
          ...oldContentEditorStateObj,
          [mapKey]: contentEditorState,
        };
        if (!contentEditorState) {
          delete newContentEditorStateObj[mapKey];
          delete newContentEditorStateObj[sourceTextKeyFor(mapKey)];
        } else if (sourceText !== undefined) {
          newContentEditorStateObj[sourceTextKeyFor(mapKey)] = sourceText;
        } else {
          delete newContentEditorStateObj[sourceTextKeyFor(mapKey)];
        }
        node.setContentEditorState(JSON.stringify(newContentEditorStateObj));
      }
    });
  };

  if (isTemplateMode) {
    return (
      <TypeaheadTemplateMenu
        type={nodeType}
        setType={commitNodeType}
        showSwitchboardTypes={isSwitchboardDoc}
      />
    );
  }
  if (nodeType === 'owner' || nodeType === 'employee') {
    if (!value && !editor.isEditable()) {
      return null;
    }
    return (
      <EmployeeSelect
        selection={value as EmployeeLdap}
        setSelection={handleEmployeeSelection}
        disabled={!editor.isEditable()}
        inputClassName={styles.employeeTypeaheadInput}
      />
    );
  } else if (nodeType === 'team_members') {
    return (
      <MultiEmployeeSelect
        selections={(value as EmployeeLdap[]) ?? []}
        setSelections={(employees) => {
          setValue(employees as EmployeeLdap[]);
          commitValue(employees as EmployeeLdap[]);
        }}
        disabled={!editor.isEditable()}
        autoFocus={false}
        inputClassName={styles.employeeTypeaheadInput}
      />
    );
  } else if (nodeType === 'date') {
    return (
      <DatePickerEditor
        disabled={!editor.isEditable()}
        date={value as string}
        setDate={handleDateSelection}
      />
    );
  } else if (nodeType === 'workstream') {
    return (
      <EditorWorkstreamTypeaheadSelection
        editor={editor}
        onSelect={(selection) =>
          handleWorkstreamSelection(selection, EditorWorkstreamType.NARRATIVE)
        }
        onDelete={(workstreamId) =>
          handleWorkstreamRemove(workstreamId, EditorWorkstreamType.NARRATIVE)
        }
        initialContentEditorState={initialContentEditorState}
        handleContentEditorStateChange={handleContentEditorStateChange}
        nodeKey={nodeKey}
      />
    );
  } else if (
    nodeType === 'switchboard-link' ||
    nodeType === 'switchboard-name' ||
    nodeType === 'switchboard-objective' ||
    nodeType === 'switchboard-objective-editable' ||
    nodeType === 'switchboard-dri' ||
    nodeType === 'switchboard-exec-sponsor' ||
    nodeType === 'switchboard-working-group' ||
    nodeType === 'switchboard-eta' ||
    nodeType === 'switchboard-subworkstreams' ||
    nodeType === 'workstream-name' ||
    nodeType === 'workstream-objective' ||
    nodeType === 'workstream-objective-editable' ||
    nodeType === 'workstream-outcomes' ||
    nodeType === 'workstream-metrics' ||
    nodeType === 'workstream-primary-lead-metric' ||
    nodeType === 'workstream-primary-lag-metric' ||
    nodeType === 'workstream-starred-milestones' ||
    nodeType === 'workstream-definition-of-done' ||
    nodeType === 'workstream-dri' ||
    nodeType === 'workstream-exec-sponsor' ||
    nodeType === 'workstream-eta' ||
    nodeType === 'workstream-subworkstreams' ||
    nodeType === 'workstream-action' ||
    nodeType === 'workstream-link'
  ) {
    return (
      <WorkstreamEditComponentWrapper
        editor={editor}
        nodeType={nodeType as WorkstreamNodeType}
        workstreamIndex={typeIndex}
        onRemoveWorkstream={(workstreamId) =>
          handleWorkstreamRemove(workstreamId, EditorWorkstreamType.NARRATIVE)
        }
        initialContentEditorState={initialContentEditorState}
        handleContentEditorStateChange={handleContentEditorStateChange}
        nodeKey={nodeKey}
      />
    );
  } else if (
    nodeType === 'contributing-workstream-name' ||
    nodeType === 'contributing-workstream-subworkstreams'
  ) {
    return (
      <ContributingWorkstreamEditComponent
        editor={editor}
        nodeType={nodeType as WorkstreamNodeType}
        workstreamIndex={typeIndex}
        onAddWorkstream={(workstream) =>
          handleWorkstreamSelection(workstream, EditorWorkstreamType.CONTRIBUTING_NARRATIVE)
        }
        onRemoveWorkstream={(workstreamId) =>
          handleWorkstreamRemove(workstreamId, EditorWorkstreamType.CONTRIBUTING_NARRATIVE)
        }
        initialContentEditorState={initialContentEditorState}
        handleContentEditorStateChange={handleContentEditorStateChange}
        nodeKey={nodeKey}
      />
    );
  } else if (
    nodeType === 'workstream-metric' ||
    nodeType === 'workstream-metric-dri' ||
    nodeType === 'workstream-metric-current-value' ||
    nodeType === 'workstream-metric-target-value' ||
    nodeType === 'workstream-metric-eta'
  ) {
    return (
      <WorkstreamMetricEditComponent
        editor={editor}
        nodeType={nodeType as WorkstreamNodeType}
        metricIndex={typeIndex}
        initialContentEditorState={initialContentEditorState}
        handleContentEditorStateChange={handleContentEditorStateChange}
        nodeKey={nodeKey}
      />
    );
  }

  return null;
};

const TypeaheadIcon = ({type}: {type: TypeaheadNodeType}) => (
  <UIIcon
    src={
      type === 'team_members'
        ? PersonMultipleLine
        : ['employee', 'owner'].includes(type)
          ? PersonLine
          : type === 'date'
            ? CalendarLine
            : type === 'workstream-starred-milestones'
              ? FlagLine
              : type === 'switchboard-working-group'
                ? PersonMultipleLine
                : type.startsWith('workstream-metric') ||
                    type.startsWith('workstream-primary') ||
                    type === 'workstream-metrics'
                  ? MetricsLine
                  : WorkstreamsLine
    }
  />
);

const TypeaheadTemplateMenu = ({
  type,
  setType,
  showSwitchboardTypes,
}: {
  type: TypeaheadNodeType;
  setType: (nodeType: TypeaheadNodeType | undefined) => void;
  showSwitchboardTypes: boolean;
}) => (
  <Menu.Wrapper onSelection={setType} shouldPropagateClickOutsideMouseEvents>
    {({getContentProps, getTriggerProps}) => (
      <>
        <Chip {...getTriggerProps()} size="small" withDropdownIcon>
          <Chip.IconAccessory>
            <TypeaheadIcon type={type} />
          </Chip.IconAccessory>

          <Chip.Content>{t(`typeahead_${type}`)}</Chip.Content>
        </Chip>
        <Menu.Content {...getContentProps()} minWidth="250px">
          <Menu.Segment>
            {['owner', 'workstream', 'employee', 'date', 'team_members'].map((mode) => (
              <Menu.ActionItem
                key={mode}
                value={mode}
                withLeftAccessory={<TypeaheadIcon type={mode as TypeaheadNodeType} />}
                withRightAccessory={type === mode && <UIIcon src={CheckmarkLine} />}
              >
                {t(`typeahead_${mode}`)}
              </Menu.ActionItem>
            ))}
          </Menu.Segment>
          {showSwitchboardTypes && (
            <Menu.Segment>
              {[
                'switchboard-name',
                'switchboard-objective',
                'switchboard-objective-editable',
                'switchboard-dri',
                'switchboard-exec-sponsor',
                'switchboard-working-group',
                'switchboard-eta',
                'switchboard-subworkstreams',
                'switchboard-link',
              ].map((mode) => (
                <Menu.ActionItem
                  key={mode}
                  value={mode}
                  withLeftAccessory={<TypeaheadIcon type={mode as TypeaheadNodeType} />}
                  withRightAccessory={type === mode && <UIIcon src={CheckmarkLine} />}
                >
                  {t(`typeahead_${mode}`)}
                </Menu.ActionItem>
              ))}
            </Menu.Segment>
          )}
          <Menu.Segment>
            {[
              'workstream-name',
              'workstream-objective',
              'workstream-objective-editable',
              'workstream-outcomes',
              'workstream-metrics',
              'workstream-definition-of-done',
              'workstream-dri',
              'workstream-exec-sponsor',
              'workstream-eta',
              'workstream-action',
              'workstream-link',
              'contributing-workstream-name',
              'contributing-workstream-subworkstreams',
            ].map((mode) => (
              <Menu.ActionItem
                key={mode}
                value={mode}
                withLeftAccessory={<TypeaheadIcon type={mode as TypeaheadNodeType} />}
                withRightAccessory={type === mode && <UIIcon src={CheckmarkLine} />}
              >
                {t(`typeahead_${mode}`)}
              </Menu.ActionItem>
            ))}
          </Menu.Segment>
          <Menu.Segment>
            {[
              'workstream-metric',
              'workstream-metric-dri',
              'workstream-metric-current-value',
              'workstream-metric-target-value',
              'workstream-metric-eta',
            ].map((mode) => (
              <Menu.ActionItem
                key={mode}
                value={mode}
                withLeftAccessory={<TypeaheadIcon type={mode as TypeaheadNodeType} />}
                withRightAccessory={type === mode && <UIIcon src={CheckmarkLine} />}
              >
                {t(`typeahead_${mode}`)}
              </Menu.ActionItem>
            ))}
          </Menu.Segment>
          <Menu.Segment>
            {[
              'workstream-primary-lead-metric',
              'workstream-primary-lag-metric',
              'workstream-starred-milestones',
            ].map((mode) => (
              <Menu.ActionItem
                key={mode}
                value={mode}
                withLeftAccessory={<TypeaheadIcon type={mode as TypeaheadNodeType} />}
                withRightAccessory={type === mode && <UIIcon src={CheckmarkLine} />}
              >
                {t(`typeahead_${mode}`)}
              </Menu.ActionItem>
            ))}
          </Menu.Segment>
          <Menu.Segment>
            <Menu.ActionItem value={undefined} withLeftAccessory={<UIIcon src={DeleteLine} />}>
              {t('typeahead_remove')}
            </Menu.ActionItem>
          </Menu.Segment>
        </Menu.Content>
      </>
    )}
  </Menu.Wrapper>
);

const EditorWorkstreamTypeaheadSelection = ({
  editor,
  onSelect,
  onDelete,
  initialContentEditorState,
  handleContentEditorStateChange,
  nodeKey,
}: {
  editor: LexicalEditor;
  onSelect: (workstream: WorkstreamBasic) => Promise<void>;
  onDelete: (workstreamId: number) => Promise<void>;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: (
    workstreamId: number,
    contentEditorState: string | undefined
  ) => void;
  nodeKey?: string;
}) => {
  const planningId = usePlanningId();
  const isPlanningDocPublished = useIsPlanningDocPublished(planningId ?? '');
  const {
    narrativeWorkstreams: workstreams,
    contributingWorkstreams,
    isLoading,
  } = useEditorWorkstreams(planningId ?? '');

  const workstreamsInfo = workstreams.map(({workstream}) => workstream);
  const contributingWorkstreamsInfo = contributingWorkstreams.map(({workstream}) => workstream);
  const workstreamsInfoString = JSON.stringify(workstreamsInfo);
  const contributingWorkstreamsInfoString = JSON.stringify(contributingWorkstreamsInfo);
  useEffect(() => {
    if (workstreamsInfo.length) {
      syncWorkstreamTableRows(editor, planningId, workstreamsInfo.length, 'workstream-name');
      hideDeletedAndPrivateWorkstreamsInWorkstreamTable(editor, workstreamsInfo, 'workstream-name');
    }

    const contributingWorkstreamsRows = isPlanningDocPublished
      ? contributingWorkstreamsInfo.length
      : contributingWorkstreamsInfo.length + 1; // +1 for add row button
    syncWorkstreamTableRows(
      editor,
      planningId,
      contributingWorkstreamsRows,
      'contributing-workstream-name'
    );
    hideDeletedAndPrivateWorkstreamsInWorkstreamTable(
      editor,
      contributingWorkstreamsInfo,
      'contributing-workstream-name'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editor,
    planningId,
    workstreamsInfoString,
    contributingWorkstreamsInfoString,
    isPlanningDocPublished,
  ]);

  if (!planningId || isLoading) return null;

  if (!workstreams.length) {
    return isPlanningDocPublished ? null : (
      <AddWorkstreamComponent editor={editor} onSelect={onSelect} archiveWorkstreams />
    );
  }

  const showAddButton =
    workstreams.length < getMaxNumOfEditorDocs(planningId) && !isPlanningDocPublished;
  const contentEditorStateObj = JSON.parse(initialContentEditorState || '{}');
  const accessibleWorkstreams = workstreams.filter((workstream) => !!workstream.is_accessible);
  return (
    <Box>
      {/* Sync all workstream data to the node for export */}
      {nodeKey && (
        <WorkstreamDataSync editor={editor} nodeKey={nodeKey} workstreams={workstreams} />
      )}

      {accessibleWorkstreams.map(({workstream_id}) => (
        <WorkstreamTypeaheadSelectionWrapper
          key={`editor-workstream__${workstream_id}`}
          editor={editor}
          workstreamId={workstream_id}
          onDelete={onDelete}
          editorStateMapJson={initialContentEditorState}
          initialContentEditorState={contentEditorStateObj[workstream_id]}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      ))}
      {showAddButton && (
        <AddWorkstreamComponent editor={editor} onSelect={onSelect} archiveWorkstreams showNoText />
      )}
    </Box>
  );
};

// Separate component to handle fetching and syncing workstream data
const WorkstreamDataSync = ({
  editor,
  nodeKey,
  workstreams,
}: {
  editor: LexicalEditor;
  nodeKey: string;
  workstreams: any[];
}) => {
  // Fetch full workstream data for each workstream
  // This component ensures hooks are called consistently (always 5 hooks)
  const workstream0 = useWorkstream(workstreams[0]?.workstream_id);
  const workstream1 = useWorkstream(workstreams[1]?.workstream_id);
  const workstream2 = useWorkstream(workstreams[2]?.workstream_id);
  const workstream3 = useWorkstream(workstreams[3]?.workstream_id);
  const workstream4 = useWorkstream(workstreams[4]?.workstream_id);

  useEffect(() => {
    // Collect all fetched workstreams
    const fullWorkstreams = [
      workstream0.workstream,
      workstream1.workstream,
      workstream2.workstream,
      workstream3.workstream,
      workstream4.workstream,
    ]
      .filter(Boolean)
      .slice(0, workstreams.length);

    if (fullWorkstreams.length > 0) {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (node && $isTypeaheadNode(node)) {
          const writableNode = node.getWritable() as any;
          writableNode.__value = fullWorkstreams;
        }
      });
    }
  }, [
    editor,
    nodeKey,
    workstreams.length,
    workstream0.workstream,
    workstream1.workstream,
    workstream2.workstream,
    workstream3.workstream,
    workstream4.workstream,
  ]);

  return null;
};
